/**
 * Хелперы прав доступа для действий с заявкой на подбор персонала
 */

/**
 * Проверяет, может ли пользователь создавать заявку на подбор персонала  
 * @param {string} userRole - Роль текущего пользователя
 * @returns {boolean}
 */
export const canCreateVacancyRequest = (userRole) => {
    const role = String(userRole || '');
    return role === 'superadmin' || role === 'admin' || role === 'hr' || role === 'leader';
};

/**
 * Проверяет, может ли пользователь согласовать\отклонить заявку на подбор персонала
 * @param {string} userRole - Роль текущего пользователя
 * @param {number} userId - ID текущего пользователя
 * @param {object} vacancyRequest - Данные заявки
 * @param {number} vacancyRequest.authorId - ID автора заявки
 * @param {number} vacancyRequest.approverHrId - ID согласующего HR
 * @returns {boolean}
 */
export const canApproveVacancyRequest = (userRole, userId, vacancyRequest = {}) => {
    const role = String(userRole || '');

    // Superadmin и admin всегда могут согласовывать
    if (role === 'superadmin' || role === 'admin') {
        return true;
    }

    // Leader может, если является автором заявки
    if (role === 'leader' && userId === vacancyRequest.authorId) {
        return true;
    }

    // HR может, если является автором заявки или согласующим HR
    if (role === 'hr' && (userId === vacancyRequest.authorId || userId === vacancyRequest.approverHrId)) {
        return true;
    }

    return false;
};

/**
 * Проверяет, может ли пользователь редактировать "Дата подачи заявки"
 * @param {string} userRole - Роль текущего пользователя
 * @returns {{canEdit: boolean, reason: string}}
 */
export const canEditSubmissionDate = (userRole) => {
    const role = String(userRole || '');
    return role === 'superadmin';
};

/**
 * Проверяет, может ли пользователь распределить заявку (назначить исполнителя)
 * @param {string} userRole - Роль текущего пользователя
 * @param {number} userId - ID текущего пользователя
 * @param {object} vacancyRequest - Данные заявки
 * @param {number} vacancyRequest.approverHrId - ID согласующего HR
 * @returns {boolean}
 */
export const canAssignVacancyRequest = (userRole, userId, vacancyRequest = {}) => {
    const role = String(userRole || '');

    // Superadmin и admin всегда могут распределять
    if (role === 'superadmin' || role === 'admin') {
        return true;
    }

    // HR может распределять только если является согласующим HR
    if (role === 'hr' && userId === vacancyRequest.approverHrId) {
        return true;
    }

    return false;
};

/**
 * Проверяет, может ли пользователь редактировать карточку заявки на подбор персонала
 * @param {string} userRole - Роль текущего пользователя
 * @param {number} userId - ID текущего пользователя
 * @param {object} vacancyRequest - Данные заявки
 * @param {number} vacancyRequest.authorId - ID автора заявки
 * @param {number} vacancyRequest.approverHrId - ID согласующего HR
 * @returns {boolean}
 */
export const canEditVacancyRequest = (userRole, userId, vacancyRequest = {}) => {
    const role = String(userRole || '');

    // Superadmin и admin всегда могут редактировать
    if (role === 'superadmin' || role === 'admin') {
        return true;
    }

    // Leader может редактировать, если является автором заявки
    if (role === 'leader' && userId == vacancyRequest.authorId) {
        return true;
    }

    // HR может редактировать, если является автором заявки или согласующим HR
    if (role === 'hr' && (userId === vacancyRequest.authorId || userId === vacancyRequest.approverHrId)) {
        return true;
    }

    return false;
};

/**
 * Проверяет, может ли пользователь отозвать заявку на подбор персонала
 * @param {string} userRole - Роль текущего пользователя
 * @param {number} userId - ID текущего пользователя
 * @param {object} vacancyRequest - Данные заявки
 * @param {number} vacancyRequest.authorId - ID автора заявки
 * @param {number} vacancyRequest.approverHrId - ID согласующего HR
 * @returns {boolean}
 */
export const canRevokeVacancyRequest = (userRole, userId, vacancyRequest = {}) => {
    const role = String(userRole || '');

    // Superadmin и admin всегда могут отзывать
    if (role === 'superadmin' || role === 'admin') {
        return true;
    }

    // Leader может отзывать, если является автором заявки
    if (role === 'leader' && userId == vacancyRequest.authorId) {
        return true;
    }

    // HR может отзывать, если является автором заявки или согласующим HR
    if (role === 'hr' && (userId === vacancyRequest.authorId || userId === vacancyRequest.approverHrId)) {
        return true;
    }

    return false;
};

/**
 * Проверяет, может ли пользователь менять статус заявки на подбор персонала
 * @param {string} userRole - Роль текущего пользователя
 * @returns {boolean}
 */
export const canChangeVacancyRequestStatus = (userRole) => {
    const role = String(userRole || '');
    return role === 'superadmin' || role === 'admin' || role === 'hr';
};

/**
 * Проверяет, может ли пользователь оставлять комментарии к вакансии
 * @param {string} userRole - Роль текущего пользователя
 * @returns {boolean}
 */
export const canCommentOnVacancy = (userRole) => {
    const role = String(userRole || '');
    return role === 'superadmin' || role === 'admin' || role === 'hr';
};

/**
 * Проверяет, может ли пользователь создавать вакансию на основе заявки на подбор персонала
 * @param {string} userRole - Роль текущего пользователя
 * @returns {boolean}
 */
export const canCreateVacancyFromRequest = (userRole) => {
    const role = String(userRole || '');
    return role === 'superadmin' || role === 'admin' || role === 'hr';
};
